import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["backgroundColor", "children", "flush", "sidebarAreaFlush", "sidebarComponent", "stickySidebar", "mainStyles"];
import styled, { css } from 'foundations-theming/utils';
import { Main } from '../Main/Main';
import { filterProps } from '../../../utils/filterProps';
import * as CustomRenderer from '../AbstractPageTemplate/customRenderer';
import { useSidebarPageContext } from '../SidebarPage/SidebarPageProvider';
import { deepMerge } from '../../utils/deepMerge';
import { BREAKPOINTS } from '../Grid/Grid.types';
import { useMapTokenPropsToCSS } from '../../../hooks/useMapTokenPropsToCSS';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SIDEBAR_FULL_WIDTH = '256px';
const SIDEBAR_INNER_WIDTH = '224px';
const Outer = styled.div.withConfig({
  displayName: "AbstractPageGridContent__Outer"
})(["background-color:", ";display:flex;flex-wrap:nowrap;margin-left:", ";margin-right:", ";@media only screen and (max-width:", "){flex-wrap:wrap;}", ";"], ({
  $backgroundColor
}) => $backgroundColor || undefined, ({
  $flush,
  theme
}) => $flush ? 0 : `-${theme.space[400]}`, ({
  $flush,
  theme
}) => $flush ? 0 : `-${theme.space[400]}`, BREAKPOINTS.sm, ({
  $cssStyles
}) => $cssStyles);
export const Sidebar = styled.div.withConfig({
  displayName: "AbstractPageGridContent__Sidebar"
})(({
  $flush,
  theme,
  $abstractPageGridContentStylesOverrides = {}
}) => {
  return deepMerge({
    flex: '0 0 33%',
    boxSizing: 'border-box',
    maxWidth: $flush ? SIDEBAR_FULL_WIDTH : SIDEBAR_INNER_WIDTH,
    paddingLeft: $flush ? 0 : theme.space['400'],
    paddingRight: $flush ? 0 : theme.space['400'],
    position: 'relative',
    [`@media only screen and (max-width: ${BREAKPOINTS.sm})`]: {
      flexBasis: '100%',
      maxWidth: 'none'
    }
  }, $abstractPageGridContentStylesOverrides);
});
const MainContainer = styled.div.withConfig({
  displayName: "AbstractPageGridContent__MainContainer"
})(["flex:1;max-width:", ";padding-left:", ";padding-right:", ";position:relative;@media only screen and (max-width:679px){max-width:", ";}@media only screen and (max-width:", "){flex-basis:100%;max-width:100%;}"], ({
  $hasSidebar,
  $flushSidebar
}) => $hasSidebar ? `calc(100% - ${$flushSidebar ? SIDEBAR_FULL_WIDTH : SIDEBAR_INNER_WIDTH})` : '100%', ({
  theme
}) => theme.space[400], ({
  theme
}) => theme.space[400], ({
  $hasSidebar
}) => $hasSidebar ? '67%' : '100%', BREAKPOINTS.sm);

// max-height was previously calc(100vh - var(--uicomponents-global-nav-height));
// it's uncertain if `--uicomponents-global-nav-height` is available to Foundations Components
const STICKY_STYLES = css(["max-height:", ";margin-left:-", ";overflow-y:auto;padding-left:", ";position:sticky;top:0;@media only screen and (max-width:", "){height:auto;}"], ({
  theme
}) => `calc(100vh - ${theme.space['1200']})`, ({
  theme
}) => theme.space[300], ({
  theme
}) => theme.space[300], BREAKPOINTS.sm);
const SidebarContainer = styled.div.withConfig({
  displayName: "AbstractPageGridContent__SidebarContainer"
})(["", ";"], props => props.$stickySidebar && STICKY_STYLES);
export const AbstractPageGridContent = _ref => {
  let {
      backgroundColor,
      children,
      flush = true,
      sidebarAreaFlush = true,
      sidebarComponent,
      stickySidebar = false,
      mainStyles
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    domProps,
    tokenProps
  } = filterProps(rest);
  const cssStyles = useMapTokenPropsToCSS(tokenProps);
  const sidebarPageContext = useSidebarPageContext();
  return /*#__PURE__*/_jsxs(Outer, Object.assign({
    $flush: sidebarAreaFlush,
    $backgroundColor: backgroundColor,
    $cssStyles: cssStyles
  }, domProps, {
    children: [sidebarComponent && /*#__PURE__*/_jsx(Sidebar, {
      $abstractPageGridContentStylesOverrides: sidebarPageContext === null || sidebarPageContext === void 0 ? void 0 : sidebarPageContext.abstractPageGridContentStylesOverrides,
      $flush: sidebarAreaFlush,
      children: /*#__PURE__*/_jsx(SidebarContainer, {
        $stickySidebar: stickySidebar,
        children: CustomRenderer.render(sidebarComponent)
      })
    }), /*#__PURE__*/_jsx(MainContainer, {
      $hasSidebar: !!sidebarComponent,
      $flushSidebar: sidebarAreaFlush,
      children: /*#__PURE__*/_jsx(Main, Object.assign({
        flush: flush
      }, mainStyles, {
        children: children
      }))
    })]
  }));
};
AbstractPageGridContent.displayName = 'AbstractPageGridContent';